'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { fetchSessionIdleTimeout } from '@/lib/settings/session-idle-timeout-actions';
import {
  DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES,
  configuredSessionIdleTimeoutMs,
} from '@/lib/settings/session-idle-timeout';
import { useAuthStore } from '@/stores/auth-store';
import { Button, Dialog, DialogFooter } from '@statura/ui';
import { closeCurrentTrackedSession, trackSession } from '@/app/(auth)/login/track-session';
import { recordSessionHeartbeat } from '@/app/(auth)/login/session-heartbeat';
import { logSessionEvent } from '@/app/(auth)/login/log-session-event';
import { toast } from 'sonner';
import { currentHostIsDemoOrStaging } from '@/lib/demo-host';
import { reportClientAuthDiagnostic } from '@/lib/auth/auth-diagnostics';
import {
  ACTIVITY_BROADCAST_DEBOUNCE_MS,
  shouldBroadcastActivity,
  shouldSampleMousemove,
} from '@/lib/session-idle-activity';

const WARNING_LEAD_MS = 5 * 60 * 1000; // show the "session expiring" warning 5 min before timeout
const WARNING_WINDOW_SECONDS = WARNING_LEAD_MS / 1000; // 300s (initial countdown placeholder)
const CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // Refresh last_seen_at every 5 min

// Loopback / private-LAN hosts where local dev and automated E2E (Momentic) run. The
// idle auto-logout is a production security control; on these hosts it is unnecessary
// and breaks long automated runs (a fresh login signs out mid-flow -> /login?reason=
// idle_timeout), so it is disabled here unless an explicit timeout is configured.
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h.endsWith('.local') ||
    /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h)
  );
}

// Effective idle-timeout in ms, or null to disable auto-logout on local dev hosts.
// The environment override is intentionally limited to local development.
function resolveIdleTimeoutMs(
  configuredMinutes: number | null,
  defaultMinutes: number,
): number | null {
  const raw = process.env.NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES;
  if (isLocalDevHost() && raw != null && raw.trim() !== '') {
    const mins = Number(raw);
    if (!Number.isFinite(mins) || mins <= 0) return null;
    return mins * 60 * 1000;
  }
  if (isLocalDevHost()) return null;
  return configuredSessionIdleTimeoutMs(configuredMinutes, defaultMinutes);
}
const CHANNEL_NAME = 'statura-session-activity';
const SESSION_ID_KEY = 'statura_session_id';

export function SessionSecurityProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? null);
  const [configuredTimeoutMinutes, setConfiguredTimeoutMinutes] = useState<number | null>(null);
  const [effectiveTimeoutMinutes, setEffectiveTimeoutMinutes] = useState(
    DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES,
  );
  const [timeoutSettingLoaded, setTimeoutSettingLoaded] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_WINDOW_SECONDS);
  const warningShownRef = useRef(false);
  // Single-fire guard. The 30s idle-check interval and the 1s warning-countdown
  // interval can both cross the timeout threshold and call handleSignOut; the
  // countdown in particular re-fires every second (it returns 0 without clearing
  // itself). Without this guard each call re-emits the SESSION_TIMEOUT audit event
  // (duplicate rows observed in production) and re-runs signOut. Sign-out is
  // terminal — we navigate to /login and unmount — so it must run at most once.
  const hasSignedOutRef = useRef(false);

  useEffect(() => {
    let active = true;
    const refreshTimeout = async () => {
      try {
        const result = await fetchSessionIdleTimeout();
        if (active) {
          setConfiguredTimeoutMinutes(result.success ? result.configuredMinutes : null);
          setEffectiveTimeoutMinutes(
            result.success ? result.effectiveMinutes : DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES,
          );
          setTimeoutSettingLoaded(true);
        }
      } catch {
        if (active) {
          setConfiguredTimeoutMinutes(null);
          setEffectiveTimeoutMinutes(DEFAULT_SESSION_IDLE_TIMEOUT_MINUTES);
          setTimeoutSettingLoaded(true);
        }
      }
    };
    void refreshTimeout();
    const interval = window.setInterval(() => void refreshTimeout(), 5 * 60 * 1000);
    const onTimeoutUpdated = () => void refreshTimeout();
    window.addEventListener('session-idle-timeout-updated', onTimeoutUpdated);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('session-idle-timeout-updated', onTimeoutUpdated);
    };
  }, [tenantId]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningShownRef.current) {
      warningShownRef.current = false;
      setShowWarning(false);
      setCountdown(WARNING_WINDOW_SECONDS);
    }
    // Broadcast to other tabs
    try {
      const bc = new BroadcastChannel(CHANNEL_NAME);
      bc.postMessage({ type: 'activity', timestamp: Date.now() });
      bc.close();
    } catch {
      // BroadcastChannel not supported — use localStorage fallback
      localStorage.setItem(CHANNEL_NAME, Date.now().toString());
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    if (hasSignedOutRef.current) return;
    hasSignedOutRef.current = true;
    reportClientAuthDiagnostic('idle_timeout', {
      source: 'session_security',
      reason: 'idle_timeout',
    });
    try {
      await logSessionEvent('SESSION_TIMEOUT', 'idle_timeout');
    } catch {
      /* don't block sign out */
    }
    try {
      await closeCurrentTrackedSession();
    } catch {
      /* stale-session cleanup remains a backstop */
    }
    try {
      const supabase = createClient();
      // scope 'local': end only THIS browser's session. The library default is
      // 'global', which revoked the user's sessions on every device, so one tab
      // idling out logged the same person out of their phone (4 Sep 2026).
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      /* ignore */
    }
    clearAuth();
    // Soft landing: return the user to where they were after re-login so an idle
    // timeout doesn't also lose their place. (Data-restore for in-progress forms
    // is the separate phased autosave work.)
    const params = new URLSearchParams({ reason: 'idle_timeout' });
    if (typeof window !== 'undefined') {
      const here = window.location.pathname + window.location.search;
      if (here && here !== '/' && !here.startsWith('/login')) params.set('next', here);
    }
    router.push(`/login?${params.toString()}`);
  }, [clearAuth, router]);

  useEffect(() => {
    let lastBroadcastAt: number | null = null;
    let lastMousemoveAt: number | null = null;

    const dismissIdleWarning = () => {
      if (!warningShownRef.current) return;
      warningShownRef.current = false;
      setShowWarning(false);
      setCountdown(WARNING_WINDOW_SECONDS);
    };

    const broadcastActivity = (timestamp: number) => {
      try {
        const bc = new BroadcastChannel(CHANNEL_NAME);
        bc.postMessage({ type: 'activity', timestamp });
        bc.close();
      } catch {
        localStorage.setItem(CHANNEL_NAME, timestamp.toString());
      }
    };

    const recordActivity = (now = Date.now()) => {
      // Always extend local idle detection — never debounce this ref.
      lastActivityRef.current = now;
      dismissIdleWarning();

      if (shouldBroadcastActivity(lastBroadcastAt, now, ACTIVITY_BROADCAST_DEBOUNCE_MS)) {
        lastBroadcastAt = now;
        broadcastActivity(now);
      }
    };

    const onImmediateActivity = () => recordActivity();
    const onMousemove = () => {
      const now = Date.now();
      if (!shouldSampleMousemove(lastMousemoveAt, now)) return;
      lastMousemoveAt = now;
      recordActivity(now);
    };

    const immediateEvents = ['keydown', 'click', 'touchstart', 'scroll'] as const;
    immediateEvents.forEach((evt) =>
      document.addEventListener(evt, onImmediateActivity, { passive: true }),
    );
    document.addEventListener('mousemove', onMousemove, { passive: true });

    const applyRemoteActivity = (timestamp: number) => {
      lastActivityRef.current = Math.max(lastActivityRef.current, timestamp);
      dismissIdleWarning();
    };

    // Listen for activity from other tabs
    let bc: BroadcastChannel | null = null;
    let onStorage: ((e: StorageEvent) => void) | null = null;
    try {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (event) => {
        if (event.data?.type === 'activity' && typeof event.data.timestamp === 'number') {
          applyRemoteActivity(event.data.timestamp);
        }
      };
    } catch {
      onStorage = (e: StorageEvent) => {
        if (e.key === CHANNEL_NAME && e.newValue) {
          const parsed = Number.parseInt(e.newValue, 10);
          if (Number.isFinite(parsed)) applyRemoteActivity(parsed);
        }
      };
      window.addEventListener('storage', onStorage);
    }

    // Idle check interval (auto-logout). Disabled on local dev / E2E hosts, or when
    // NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES is set to 0.
    const idleTimeoutMs = timeoutSettingLoaded
      ? resolveIdleTimeoutMs(configuredTimeoutMinutes, effectiveTimeoutMinutes)
      : null;
    const warningThresholdMs =
      idleTimeoutMs != null ? Math.max(0, idleTimeoutMs - WARNING_LEAD_MS) : 0;
    const idleCheckInterval =
      idleTimeoutMs == null
        ? null
        : setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;
            if (elapsed >= idleTimeoutMs) {
              handleSignOut();
            } else if (elapsed >= warningThresholdMs && !warningShownRef.current) {
              warningShownRef.current = true;
              setShowWarning(true);
              setCountdown(Math.ceil((idleTimeoutMs - elapsed) / 1000));
            }
          }, CHECK_INTERVAL_MS);

    // Track concurrent sessions ONCE per browser session. Previously the guard
    // key was only set when existingSessions>0, so users with no other sessions
    // re-ran trackSession on every navigation — inserting a new user_sessions row
    // each time (unbounded growth + inflated "other active sessions" counts).
    const sessionGateKey = 'statura_session_tracked';
    if (!currentHostIsDemoOrStaging() && !sessionStorage.getItem(sessionGateKey)) {
      sessionStorage.setItem(sessionGateKey, '1');
      trackSession()
        .then((result) => {
          if (result.sessionId) sessionStorage.setItem(SESSION_ID_KEY, result.sessionId);
          if (result.existingSessions > 0) {
            toast.info(
              `You have ${result.existingSessions} other active session${result.existingSessions === 1 ? '' : 's'} on: ${result.devices.join(', ')}`,
              { duration: 8000 },
            );
          }
        })
        .catch(() => {
          // Allow a retry on a later mount if tracking failed.
          sessionStorage.removeItem(sessionGateKey);
        });
    }

    // Heartbeat: keep this session's last_seen_at fresh so it isn't swept as
    // stale (and so the "other active sessions" count stays accurate).
    const heartbeatInterval = setInterval(() => {
      const sid = sessionStorage.getItem(SESSION_ID_KEY);
      if (sid) void recordSessionHeartbeat(sid).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      immediateEvents.forEach((evt) => document.removeEventListener(evt, onImmediateActivity));
      document.removeEventListener('mousemove', onMousemove);
      if (idleCheckInterval) clearInterval(idleCheckInterval);
      clearInterval(heartbeatInterval);
      bc?.close();
      if (onStorage) window.removeEventListener('storage', onStorage);
    };
  }, [configuredTimeoutMinutes, effectiveTimeoutMinutes, handleSignOut, timeoutSettingLoaded]);

  // Countdown timer (runs only when warning is shown)
  useEffect(() => {
    if (!showWarning) return;
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [showWarning, handleSignOut]);

  return (
    <>
      {children}
      {showWarning && (
        <Dialog
          role="alertdialog"
          open
          onClose={() => undefined}
          title="Session expiring"
          size="sm"
          overlayClassName="z-[9999]"
          closeOnBackdrop={false}
          closeOnEscape={false}
          showCloseButton={false}
        >
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-status-warning-bg">
              <svg
                className="h-6 w-6 text-status-warning-fg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>
            <p className="font-body text-body-sm text-text-body">
              Your session will expire in{' '}
              <span className="font-mono font-bold text-status-warning-fg">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
              </span>{' '}
              due to inactivity.
            </p>
            <DialogFooter>
              <Button type="button" onClick={handleSignOut} variant="secondary">
                Log out
              </Button>
              <Button type="button" onClick={resetActivity} variant="primary">
                Stay logged in
              </Button>
            </DialogFooter>
          </div>
        </Dialog>
      )}
    </>
  );
}
